/**
 * CyberSells Cart Projection Module
 * Shows preview of stat changes and XP gains from cart items
 */

(function() {
  'use strict';

  /**
   * Calculate projected stats and XP from cart
   */
  function calculateCartProjection() {
    if (!window.CyberSells_playerData || !window.CyberSells_stats) {
      return null;
    }

    const playerData = window.CyberSells_playerData.loadPlayerData();
    const cart = getCartData();

    if (!cart || !cart.items || cart.items.length === 0) {
      return {
        isEmpty: true,
        playerData,
        projection: null
      };
    }

    // Calculate stat deltas from cart items
    const statDeltas = {};
    let totalXpGain = 0;
    const cartItemsWithSlots = [];

    cart.items.forEach(item => {
      // Get stats and equipment slot from item
      const { stats, equipmentSlot } = parseItemStats(item);
      
      // Store item with slot info
      cartItemsWithSlots.push({
        ...item,
        equipmentSlot: equipmentSlot || 'misc',
        parsedStats: stats
      });
      
      for (const [stat, value] of Object.entries(stats)) {
        statDeltas[stat] = (statDeltas[stat] || 0) + (value * item.quantity);
      }

      // Calculate XP from item price
      const itemTotal = item.final_line_price || (item.price * item.quantity);
      totalXpGain += window.CyberSells_stats.calculatePurchaseXp(itemTotal);
    });

    // Calculate projected stats
    const currentStats = { ...playerData.stats };
    const projectedStats = { ...playerData.stats };
    
    for (const [stat, delta] of Object.entries(statDeltas)) {
      if (projectedStats[stat] !== undefined) {
        projectedStats[stat] += delta;
      }
    }

    // Calculate projected level
    const currentLevel = playerData.level;
    const currentXp = playerData.xp;
    const projectedXp = currentXp + totalXpGain;
    const projectedLevel = window.CyberSells_stats.calculateLevelFromXp(projectedXp);
    const willLevelUp = projectedLevel > currentLevel;

    // Calculate combo bonuses (if multiple items with same stat)
    const comboBonuses = calculateComboBonuses(statDeltas);

    return {
      isEmpty: false,
      playerData,
      cartTotal: cart.total_price,
      itemCount: cart.item_count,
      cartItems: cartItemsWithSlots,
      
      // Current state
      currentStats,
      currentLevel,
      currentXp,
      
      // Projected state
      projectedStats,
      projectedLevel,
      projectedXp,
      
      // Deltas
      statDeltas,
      totalXpGain,
      levelGain: projectedLevel - currentLevel,
      willLevelUp,
      
      // Bonuses
      comboBonuses,
      
      // Display
      hasStatChanges: Object.keys(statDeltas).length > 0
    };
  }

  /**
   * Parse item stats and equipment slot from cart item data
   */
  function parseItemStats(item) {
    const stats = {};
    let equipmentSlot = null;

    // Check for stats in properties (from metafields)
    if (item.properties) {
      for (const [key, value] of Object.entries(item.properties)) {
        // Equipment slot
        if (key === 'equipment_slot' || key === '_equipment_slot') {
          equipmentSlot = value;
          continue;
        }
        
        // Look for stat keys (str, agi, int, etc.)
        const statKey = key.toLowerCase();
        const statValue = parseInt(value);
        
        if (!isNaN(statValue) && isValidStat(statKey)) {
          stats[statKey] = statValue;
        }
      }
    }

    // Check for stats in variant properties
    if (item.variant_options) {
      item.variant_options.forEach(option => {
        const statKey = option.toLowerCase();
        if (isValidStat(statKey)) {
          stats[statKey] = 1; // Default value
        }
      });
    }

    // Check for stats data attribute (from product metafield)
    if (item.stats) {
      try {
        const itemStats = typeof item.stats === 'string' ? JSON.parse(item.stats) : item.stats;
        Object.assign(stats, itemStats);
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Check for equipment_slot in item data
    if (item.equipment_slot) {
      equipmentSlot = item.equipment_slot;
    }

    return { stats, equipmentSlot };
  }

  /**
   * Check if stat key is valid
   */
  function isValidStat(key) {
    const validStats = ['str', 'agi', 'int', 'vit', 'luk', 'damage', 'defense', 'speed', 'charisma', 'stealth', 'perception', 'endurance', 'tech', 'style'];
    return validStats.includes(key);
  }

  /**
   * Calculate combo bonuses for multiple items with same stat
   */
  function calculateComboBonuses(statDeltas) {
    const bonuses = {};
    
    for (const [stat, total] of Object.entries(statDeltas)) {
      if (total >= 5) {
        bonuses[stat] = Math.floor(total * 0.1); // 10% bonus for 5+ points
      }
    }

    return bonuses;
  }

  /**
   * Get cart data from Shopify
   */
  function getCartData() {
    // Try to get from window.cartData first (if available)
    if (window.cartData) {
      return window.cartData;
    }

    // Try to parse from cart JSON endpoint (requires fetch)
    // For now, return null and rely on event-based updates
    return null;
  }

  /**
   * Fetch cart data from Shopify API
   */
  async function fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      if (window.cyberSellsConfig?.debug) {
        console.error('[CyberSells] Error fetching cart:', error);
      }
      return null;
    }
  }

  /**
   * Update cart projection display
   */
  async function updateCartProjection() {
    const widget = document.querySelector('[data-cs-cart-projection]');
    if (!widget) return;

    // Fetch fresh cart data
    const cart = await fetchCartData();
    if (cart) {
      window.cartData = cart; // Cache for getCartData
    }

    const projection = calculateCartProjection();
    
    if (!projection) {
      widget.innerHTML = '<div class="cs-cart-projection__error">Unable to load projection</div>';
      return;
    }

    if (projection.isEmpty) {
      widget.innerHTML = '<div class="cs-cart-projection__empty">Add items to see your power-up preview!</div>';
      return;
    }

    // Render projection
    widget.innerHTML = renderProjection(projection);
  }

  /**
   * Group items by equipment slot
   */
  function groupItemsBySlot(cartItems) {
    const grouped = {};
    
    cartItems.forEach(item => {
      const slot = item.equipmentSlot || 'misc';
      if (!grouped[slot]) {
        grouped[slot] = [];
      }
      grouped[slot].push(item);
    });

    return grouped;
  }

  /**
   * Render a single cart item
   */
  function renderCartItem(slot, item) {
    const slotIcons = {
      head: '??',
      chest: '??',
      legs: '??',
      hands: '??',
      feet: '??',
      accessory1: '?',
      accessory2: '??',
      misc: '??'
    };

    const slotLabels = {
      head: 'HEAD',
      chest: 'CHEST',
      legs: 'LEGS',
      hands: 'HANDS',
      feet: 'FEET',
      accessory1: 'ACC 1',
      accessory2: 'ACC 2',
      misc: 'MISC'
    };

    let html = '<div class="cs-cart-projection__slot-item">';
    html += `<div class="cs-cart-projection__slot-icon">${slotIcons[slot]}</div>`;
    html += '<div class="cs-cart-projection__slot-info">';
    html += `<div class="cs-cart-projection__slot-label">${slotLabels[slot]}</div>`;
    html += `<div class="cs-cart-projection__slot-title">`;
    html += `${item.product_title || item.title}`;
    if (item.quantity > 1) {
      html += `<span class="cs-cart-projection__slot-qty">x${item.quantity}</span>`;
    }
    html += `</div>`;
    html += '</div>';
    html += '</div>';

    return html;
  }

  /**
   * Render projection HTML - Equipment Console Style
   */
  function renderProjection(projection) {
    const { cartItems, statDeltas, totalXpGain, willLevelUp, projectedLevel, currentLevel, comboBonuses, hasStatChanges } = projection;

    let html = '<div class="cs-cart-projection">';
    
    // Header
    html += '<div class="cs-cart-projection__header">';
    html += '<h3 class="cs-cart-projection__title"><span class="cs-glow-cyan">? POWER-UP PREVIEW</span></h3>';
    html += '</div>';

    html += '<div class="cs-cart-projection__body">';
    html += '<div class="cs-cart-projection__grid">';

    // LEFT PANEL: Equipment Items
    html += '<div class="cs-cart-projection__items">';
    html += '<div class="cs-panel-header"><span class="cs-glow-lime">? CART ITEMS</span></div>';
    html += '<div class="cs-cart-projection__slots">';

    // Group items by slot
    const itemsBySlot = groupItemsBySlot(cartItems);
    
    for (const [slot, items] of Object.entries(itemsBySlot)) {
      items.forEach(item => {
        html += renderCartItem(slot, item);
      });
    }

    html += '</div></div>'; // Close items panel

    // RIGHT PANEL: Stats
    html += '<div class="cs-cart-projection__stats-panel">';

    // XP
    if (totalXpGain > 0) {
      html += '<div class="cs-cart-projection__xp">';
      html += '<div class="cs-cart-projection__xp-content">';
      html += `<span class="cs-cart-projection__xp-label">XP GAIN</span>`;
      html += `<span class="cs-cart-projection__xp-value">+${totalXpGain.toLocaleString()}</span>`;
      html += '</div></div>';
    }

    // Level Up
    if (willLevelUp) {
      const levelGain = projectedLevel - currentLevel;
      html += '<div class="cs-cart-projection__alert cs-cart-projection__alert--level-up">';
      html += '<span class="cs-cart-projection__alert-icon">??</span>';
      html += `<span class="cs-cart-projection__alert-text">LEVEL UP! ${currentLevel} ? ${projectedLevel}</span>`;
      if (levelGain > 1) {
        html += `<span class="cs-cart-projection__alert-bonus">+${levelGain} LEVELS!</span>`;
      }
      html += '</div>';
    }

    // Stats
    if (hasStatChanges) {
      html += '<div class="cs-cart-projection__stats">';
      html += '<div class="cs-cart-projection__stats-title"><span class="cs-glow-lime">? STAT BOOSTS</span></div>';
      html += '<div class="cs-cart-projection__stats-grid">';

      const statIcons = {
        str: '?', agi: '?', int: '??', vit: '?', luk: '??',
        damage: '??', defense: '??', speed: '??', tech: '?',
        charisma: '?', style: '??', perception: '??', endurance: '??',
        stealth: '??'
      };

      const statColors = {
        str: 'red', agi: 'lime', int: 'cyan', vit: 'red',
        luk: 'lime', damage: 'red', defense: 'cyan', speed: 'lime',
        tech: 'cyan', charisma: 'purple', style: 'purple',
        perception: 'cyan', endurance: 'red', stealth: 'purple'
      };

      for (const [stat, delta] of Object.entries(statDeltas)) {
        if (delta > 0) {
          const icon = statIcons[stat] || '?';
          const color = statColors[stat] || 'cyan';
          const hasCombo = comboBonuses[stat] && comboBonuses[stat] > 0;
          
          html += `<div class="cs-cart-projection__stat-item${hasCombo ? ' cs-cart-projection__stat-item--combo' : ''}">`;
          html += `<span class="cs-cart-projection__stat-icon cs-glow-${color}">${icon}</span>`;
          html += `<span class="cs-cart-projection__stat-label">${stat.toUpperCase()}</span>`;
          html += `<span class="cs-cart-projection__stat-value">${delta}</span>`;
          html += `</div>`;
        }
      }

      html += '</div></div>'; // Close stats
    }

    // Summary
    html += '<div class="cs-cart-projection__summary">Complete this purchase to power up your character</div>';
    
    html += '</div>'; // Close stats panel
    html += '</div>'; // Close grid
    html += '</div>'; // Close body
    html += '</div>'; // Close projection

    return html;
  }

  // Expose module functions
  window.CyberSells_cartProjection = {
    calculateCartProjection,
    updateCartProjection,
    fetchCartData
  };

  // Listen for cart updates
  document.addEventListener('cart:updated', () => {
    updateCartProjection();
  });

  // Also listen for Shopify theme cart updates
  document.addEventListener('theme:cart:change', () => {
    updateCartProjection();
  });

  console.log('[CyberSells] Cart Projection module loaded');

})();
